<i18n>
{
  "en": {
    "zones": "Zones",
    "prices": "Prices",
    "saleFormatError": "The price breaks the accepted main product/half product price ratio {0}.",
    "PriceRangeError": "The price is not in range between maximum and minimum price allowed."
  },
  "es": {
    "zones": "Zonas",
    "prices": "Precios",
    "saleFormatError": "El precio no cumple el ratio aceptado de precios {0} entre producto/medio producto.",
    "PriceRangeError": "El precio '{price}' no está dentro del rango de precio máximo y mínimo permitido."
  }
}
</i18n>

<template>
  <div>
    <v-data-table :headers="tableHeaders" :items="tableElements" mobile-breakpoint="0"
                  class="elevation-1" disable-sort disable-filtering disable-pagination
                  hide-default-footer>
      <template v-slot:body="{ items, headers }">
        <tbody>
        <tr v-for="(item, idx) in items" :key="idx">
          <td v-for="(header, idy) in headers" :key="idy" :class="getValueClass(idx, idy)">
            <div v-if="isCellEditable(idy, item[header.value])">
              <v-edit-dialog @close="onClose(idx, idy)">
                {{currency.hasLeftPosition() ? currency.symbol : ''}}
                {{ item[header.value].price }}
                {{currency.hasRightPosition() ? currency.symbol : ''}}
                <template v-slot:input>
                  <v-text-field v-model="item[header.value].price" single-line
                                :prefix="currency.hasLeftPosition() ? currency.symbol : ''"
                                :suffix="currency.hasRightPosition() ? currency.symbol : ''"
                                placeholder="0.00"
                                type="number" :disabled="disabled"/>
                </template>
              </v-edit-dialog>
            </div>
            <v-tooltip v-else top>
              <template v-slot:activator="{ on, attrs }">
                <div class="text-truncate zone-label" v-on="on" v-bind="attrs">
                  {{ item[header.value] }}
                </div>
              </template>
              <span>{{ item[header.value] }}</span>
            </v-tooltip>
          </td>
        </tr>
        </tbody>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import { MapActions, MapGetters } from '@core/helpers/decorators';
import { createWarningNotification } from '@ulibs/unnotificationsqueue';

@Component
@MapGetters(['getSaleZoneNamesByRateGuid', 'getServiceNameByGuid', 'currency'])
@MapActions(['addNotification'])
export default class SingleServicePriceEditTable extends Vue {
  @Prop({ Object, default: {} }) groupObject;

  @Prop({ Object, default: {} }) parentGroupObject;

  @Prop({ Boolean, default: false }) disabled;

  @Prop({ Number, default: 1.0 }) maxWeightHalfPortionRate;

  @Prop({ Number, default: 0 }) minimumPriceAllowed;

  @Prop({ Number, default: null }) maximumPriceAllowed;

  initialTableElements = null;

  get tableHeaders() {
    return [{
      text: this.$t('zones'),
      align: 'start',
      sortable: false,
      value: 'name',
      divider: true
    },
    // Show only the first price
    {
      text: this.$t('prices'),
      value: '1',
      divider: true,
      align: 'end'
    }];
  }

  get tableElements() {
    const services = Object.keys(this.groupObject).map((serGuid) => this.groupObject[serGuid]);
    const elementsToDraw = [];
    services.forEach((serviceObject, index) => this.generateElementArray(serviceObject, index, elementsToDraw, this.groupObject));
    return elementsToDraw;
  }

  get parentTableElements() {
    if (this.parentGroupObject) {
      const services = Object.keys(this.parentGroupObject).map((serGuid) => this.parentGroupObject[serGuid]);
      const parentElements = [];
      services.forEach((serviceObject, index) => this.generateElementArray(serviceObject, index, parentElements, this.parentGroupObject));
      return parentElements;
    }
    return null;
  }

  get allSalesFormatsRulesByPosition() {
    const rules = new Map();
    for (let idx = 0; idx < this.tableElements.length; ++idx) {
      rules.set(idx, new Map());
      for (let idy = 0; idy < this.tableHeaders.length; ++idy) {
        rules.get(idx).set(idy, this.checkSaleFormatRuleDependingParent(idx, idy));
      }
    }
    return rules;
  }

  get SaleFormatErrorMessage() {
    return this.$t('saleFormatError', [this.maxWeightHalfPortionRate]);
  }

  mounted() {
    this.saveLocalObject();
  }

  created() {
    this.initialTableElements = this.createInitialPrices();
  }

  createInitialPrices() {
    if (this.groupObject === null) return null;
    const services = Object.keys(this.groupObject).map((serGuid) => this.groupObject[serGuid]);
    const initialElements = [];
    services.forEach((serviceObject, index) => this.generateElementArray(serviceObject, index, initialElements, this.groupObject));
    return initialElements;
  }

  saveLocalObject() {
    const newGroupObject = this.buildGroupObject(this.tableElements);
    this.$emit('price-change', newGroupObject);
  }

  buildGroupObject(elements) {
    const demandArray = this.generateDemandArrayFromElements(elements);
    return this.generateObjectFromDemandArray(demandArray);
  }

  generateDemandArrayFromElements(elements) {
    const demandArray = [];
    elements.forEach((el) => {
      const keys = Object.keys(el);
      el.serviceArray.forEach((serviceGuid) => {
        const serviceObject = {};
        serviceObject[serviceGuid] = {};
        serviceObject[serviceGuid][el.saleRateGuid] = { price: 0.0, previous_price: 0.0 };
        // Overwrite all prices with the price in index 0, the one that was just edited
        serviceObject[serviceGuid][el.saleRateGuid].price = parseFloat(el[keys[0]].price || 0).toFixed(2);
        serviceObject[serviceGuid][el.saleRateGuid].previous_price = parseFloat(el[keys[0]].previous_price || 0).toFixed(2);
        demandArray.push(serviceObject);
      });
    });
    return demandArray;
  }

  generateObjectFromDemandArray(demandArray) {
    const groupObject = {};
    demandArray.forEach((object) => {
      this.copyValuesToGroupObject(groupObject, object);
    });
    return groupObject;
  }

  copyValuesToGroupObject(groupObject, demandArrayObject) {
    Object.keys(demandArrayObject).forEach((serviceGuid) => {
      Object.keys(demandArrayObject[serviceGuid]).forEach((rateGuid) => {
        // if it's the first time we've seen that service, we create the object
        if (!(serviceGuid in groupObject)) groupObject[serviceGuid] = {};
        // else we edit the existing service object with the rate and its value
        groupObject[serviceGuid][rateGuid] = demandArrayObject[serviceGuid][rateGuid];
      });
    });
  }

  generateElementArray(serviceObject, index, arrToFill, groupObject) {
    const serviceGuid = Object.keys(groupObject)[index];

    Object.keys(serviceObject).forEach((saleRateGuid) => {
      const saleZoneNames = this.getSaleZoneNamesByRateGuid(saleRateGuid);
      let element = arrToFill.find((el) => el.name === saleZoneNames);
      if (!element) {
        element = {};
        element.name = saleZoneNames;
        element.saleRateGuid = saleRateGuid;
        element.serviceArray = [];
        element[index + 1] = {
          price: groupObject[serviceGuid][saleRateGuid].price,
          previous_price: groupObject[serviceGuid][saleRateGuid].previous_price
        };
        element.serviceArray.push(serviceGuid);
        arrToFill.push(element);
      } else {
        element.serviceArray.push(serviceGuid);
        element[index + 1] = {
          price: groupObject[serviceGuid][saleRateGuid].price,
          previous_price: groupObject[serviceGuid][saleRateGuid].previous_price
        };
      }
    });
  }

  checkSaleFormatRuleDependingParent(itemRow, headerCol) {
    let rule;
    if (this.parentGroupObject) {
      rule = (childValue) => this.isSaleFormatPriceValid(this.parentTableElements[itemRow][this.tableHeaders[headerCol].value].price, childValue);
    } else {
      rule = () => true;
    }
    return rule;
  }

  isSaleFormatPriceValid(mainProductPrice, subordinateProductPrice) {
    return subordinateProductPrice <= mainProductPrice * this.maxWeightHalfPortionRate;
  }

  getValueClass(idx, idy) {
    if (idy === 0) return {};

    const price = this.getPrice(idx, idy);
    const { isSaleFormatRuleValid, isPriceRangeRuleValid } = this.getRulesOutput(price, idx, idy);
    const isPriceValid = (isSaleFormatRuleValid && isPriceRangeRuleValid) || price === undefined;
    return {
      'error-price': !isPriceValid,
      'modified-price': this.isModifyCells(idx, idy),
    };
  }

  getPrice(idx, idy) {
    return parseFloat(this.tableElements[idx][idy].price);
  }

  getInitialPrice(idx, idy) {
    return parseFloat(this.initialTableElements[idx][idy].price);
  }

  getRulesOutput(price, idx, idy) {
    const isSaleFormatRuleValid = this.allSalesFormatsRulesByPosition.get(idx).get(idy)(price);
    const isPriceRangeRuleValid = this.isPriceInRange(price);
    return { isSaleFormatRuleValid, isPriceRangeRuleValid };
  }

  isPriceInRange(price) {
    let isInRange = true;
    if (this.maximumPriceAllowed) isInRange = isInRange && price <= this.maximumPriceAllowed;
    return isInRange && this.minimumPriceAllowed <= price;
  }

  isModifyCells(idx, idy) {
    return (this.isCellDefined(idy)) ? this.getPrice(idx, idy) !== this.getInitialPrice(idx, idy) : false;
  }

  isCellDefined(idy) {
    return idy !== 0 && this.initialTableElements !== null;
  }

  isCellEditable(idy, cell) {
    return idy && (cell && cell.price) !== undefined;
  }

  onClose(idx, idy) {
    const price = this.getPrice(idx, idy);
    const rulesOutput = this.getRulesOutput(price, idx, idy);
    this.showErrorNotificationIfNecessary(price, rulesOutput);
    this.saveLocalObject();
  }

  showErrorNotificationIfNecessary(price, rulesResult) {
    const { isSaleFormatRuleValid, isPriceRangeRuleValid } = rulesResult;
    if (!isSaleFormatRuleValid || !isPriceRangeRuleValid) {
      const errorMessage = (!isSaleFormatRuleValid) ? 'saleFormatError' : 'PriceRangeError';
      this.addNotification(createWarningNotification(this.$t(`${errorMessage}`, { price }),
        {}, 3000));
    }
  }
}
</script>
<style scoped>
.zone-label {
  max-width: 30vw;
}

th, td {
  border-right: 1px solid #ddd;
}

td + td {
  text-align: right;
}

.error-price {
  color: #e74444;
}

.modified-price {
  background: #fcfbaf
}
</style>
